import {
  HemisphereLight,
  DirectionalLight,
  AmbientLight,
  Color,
  type Scene,
  type Object3D,
} from 'three';

/**
 * Lighting rig per design doc:
 *   - hemisphere fill for pleasant ambient gradient (sky vs ground)
 *   - one directional key light with soft shadows
 *   - dynamic sun: a 0..1 slider (morning → midday → evening) repositions
 *     the directional light along an arc and re-tints all three lights
 *     so the scene "feels" like the chosen time of day.
 *
 * The goal is readability with mood, not drama.
 */
export interface LightingRig {
  group: Object3D;
  key: DirectionalLight;
  hemisphere: HemisphereLight;
  ambient: AmbientLight;
  /** 0 = sunrise, 0.5 = midday, 1 = sunset. Re-tints lights and moves key. */
  setSun(t: number): void;
  dispose(): void;
}

export function installLighting(scene: Scene): LightingRig {
  const hemisphere = new HemisphereLight(0xe8eef5, 0x1b2028, 0.85);

  const key = new DirectionalLight(0xfff2d6, 2.0);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.near = 0.5;
  key.shadow.camera.far = 60;
  key.shadow.camera.left = -20;
  key.shadow.camera.right = 20;
  key.shadow.camera.top = 20;
  key.shadow.camera.bottom = -20;
  // Two-pronged anti-leak: a small constant bias hides the usual self-shadow
  // acne on big walls, and a normalBias offset along each surface's normal
  // keeps shadows of upper-story objects from bleeding through the relatively
  // thin (0.18 m) floor slabs onto whatever is below.
  key.shadow.bias = -0.0002;
  key.shadow.normalBias = 0.05;

  const ambient = new AmbientLight(0xffffff, 0.15);

  scene.add(hemisphere, key, ambient);

  const rig: LightingRig = {
    group: key,
    key,
    hemisphere,
    ambient,
    setSun(t) {
      applySun(rig, clamp(t, 0, 1));
    },
    dispose() {
      scene.remove(hemisphere, key, ambient);
      hemisphere.dispose();
      key.dispose();
      ambient.dispose();
    },
  };
  rig.setSun(0.5);
  return rig;
}

const RADIUS = 18;
const SUNRISE_TINT = new Color('#ffb070');
const MIDDAY_TINT = new Color('#fff2d6');
const SUNSET_TINT = new Color('#ff8a55');
const SKY_DAY = new Color('#cfe2f5');
const SKY_DAWN = new Color('#f6c79e');
const SKY_DUSK = new Color('#f0a875');
const GROUND_BASE = new Color('#1b2028');

function applySun(rig: LightingRig, t: number): void {
  // Arc: azimuth sweeps east (-X) → south (+Z, top of sky) → west (+X).
  const azimuth = Math.PI * (t - 0.5); // -π/2 .. +π/2
  // Elevation: peaks at t=0.5, low at t=0 and t=1.
  const elev = Math.sin(t * Math.PI) * (Math.PI * 0.45) + 0.1;
  rig.key.position.set(
    Math.sin(azimuth) * RADIUS * Math.cos(elev),
    Math.sin(elev) * RADIUS,
    Math.cos(azimuth) * RADIUS * Math.cos(elev) - 6,
  );
  rig.key.target.position.set(0, 0, 0);
  rig.key.target.updateMatrixWorld();

  // Tint the key light.
  let tint: Color;
  if (t < 0.5) {
    tint = mixColor(SUNRISE_TINT, MIDDAY_TINT, smooth01(t * 2));
  } else {
    tint = mixColor(MIDDAY_TINT, SUNSET_TINT, smooth01((t - 0.5) * 2));
  }
  rig.key.color.copy(tint);
  // Intensity dips at sunrise/sunset.
  rig.key.intensity = lerp(0.9, 2.4, Math.sin(t * Math.PI));

  // Hemisphere sky/ground colours follow the same arc.
  const sky =
    t < 0.5
      ? mixColor(SKY_DAWN, SKY_DAY, smooth01(t * 2))
      : mixColor(SKY_DAY, SKY_DUSK, smooth01((t - 0.5) * 2));
  rig.hemisphere.color.copy(sky);
  rig.hemisphere.groundColor.copy(GROUND_BASE);
  rig.hemisphere.intensity = lerp(0.55, 1.1, Math.sin(t * Math.PI));

  rig.ambient.intensity = lerp(0.08, 0.22, Math.sin(t * Math.PI));
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function smooth01(t: number): number {
  return t * t * (3 - 2 * t);
}

function mixColor(a: Color, b: Color, t: number): Color {
  return new Color(
    lerp(a.r, b.r, t),
    lerp(a.g, b.g, t),
    lerp(a.b, b.b, t),
  );
}
